/**
 * React state for Flux Agent live voice ({@link FluxAgentLiveSession}): phase, captions, timings,
 * mute, and a fallback signal for the older voice pipeline.
 *
 * @module apps/web/src/components/flux-agent/useFluxAgentLiveVoice
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FluxAgentLiveSession,
  canRunLiveVoice,
  type LiveCaption,
  type LiveDeps,
  type LivePhase,
  type LiveTimings,
} from '@/lib/fluxAgentLive';

/** Options for {@link useFluxAgentLiveVoice}. */
export interface LiveVoiceOptions {
  /** Server reports live voice (`status.voice.live`); false skips straight to the fallback. */
  available: boolean;
  /** Resolve the conversation to talk in, creating one if needed; `null` aborts the start. */
  ensureConversation: () => Promise<string | null>;
  /** Start the older transcribe → chat → speak voice mode instead. */
  onFallback: () => void;
  /** Called after the session closed, so the page can reload the stored messages. */
  onClosed?: (conversationId: string) => void;
  /** Injected browser dependencies (tests). */
  deps?: LiveDeps;
}

/** Live voice state and actions for the page. */
export interface FluxAgentLiveVoice {
  /** Current phase; `idle` before the first start and after a fallback. */
  phase: LivePhase;
  /** Caption bubbles of the current session. */
  captions: LiveCaption[];
  /** Measured connection latencies. */
  timings: LiveTimings;
  /** Last user-presentable problem, or null. */
  error: string | null;
  /** Whether the microphone is muted locally. */
  muted: boolean;
  /** True while a session is connecting, live, reconnecting or closing. */
  active: boolean;
  /** Start from a click or tap (iOS only unlocks audio inside the gesture). */
  start: () => Promise<void>;
  /** Finish the conversation gracefully. */
  stop: () => Promise<void>;
  /** Mute or unmute the microphone without ending the session. */
  setMuted: (muted: boolean) => void;
}

/** Phases during which a session holds the microphone. */
const ACTIVE: LivePhase[] = ['connecting', 'live', 'reconnecting', 'closing'];

/** Run Flux Agent live voice, falling back to the older pipeline when live voice cannot start. */
export function useFluxAgentLiveVoice({
  available,
  ensureConversation,
  onFallback,
  onClosed,
  deps,
}: LiveVoiceOptions): FluxAgentLiveVoice {
  const [phase, setPhase] = useState<LivePhase>('idle');
  const [captions, setCaptions] = useState<LiveCaption[]>([]);
  const [timings, setTimings] = useState<LiveTimings>({});
  const [error, setError] = useState<string | null>(null);
  const [muted, setMutedState] = useState(false);
  const session = useRef<FluxAgentLiveSession | null>(null);
  const callbacks = useRef({ onFallback, onClosed, ensureConversation });
  callbacks.current = { onFallback, onClosed, ensureConversation };

  const start = useCallback(async () => {
    if (session.current && ACTIVE.includes(session.current.phase)) return;
    setError(null);
    if (!available || !canRunLiveVoice()) {
      callbacks.current.onFallback();
      return;
    }
    const conversationId = await callbacks.current.ensureConversation();
    if (!conversationId) return;
    setCaptions([]);
    setTimings({});
    setMutedState(false);
    const created: FluxAgentLiveSession = new FluxAgentLiveSession(
      {
        conversationId,
        onPhase: next => session.current === created && setPhase(next),
        onCaptions: next => session.current === created && setCaptions(next),
        onTimings: next => session.current === created && setTimings(next),
        onError: (message, fallback) => {
          if (session.current !== created) return;
          if (fallback) {
            setPhase('idle');
            callbacks.current.onFallback();
          } else setError(message);
        },
        onClosed: () => callbacks.current.onClosed?.(conversationId),
      },
      deps
    );
    session.current = created;
    await created.start();
  }, [available, deps]);

  const stop = useCallback(async () => {
    await session.current?.stop();
  }, []);

  const setMuted = useCallback((next: boolean) => {
    session.current?.setMuted(next);
    setMutedState(next);
  }, []);

  // Leaving the page or unmounting ends the session so it stops billing.
  useEffect(() => {
    const end = () => void session.current?.stop();
    window.addEventListener('pagehide', end);
    return () => {
      window.removeEventListener('pagehide', end);
      end();
    };
  }, []);

  return {
    phase,
    captions,
    timings,
    error,
    muted,
    active: ACTIVE.includes(phase),
    start,
    stop,
    setMuted,
  };
}
